import qs from 'qs';

/**
 * Wire-format contract for query-string parameters.
 *
 * HTTP query strings are text. JS booleans, Date instances, null/undefined and
 * arrays each need an explicit textual encoding that the Laravel backend can
 * reliably decode. This module is the single source of truth for that
 * encoding on the frontend.
 *
 * See docs/api/WIRE-FORMAT.md for the full contract.
 */

export type ParamPrimitive = string | number | boolean | Date | null | undefined;
export type ParamValue = ParamPrimitive | ParamPrimitive[];
export type Params = Record<string, ParamValue>;

export function serializeParams(params: unknown): string {
  if (!isPlainParams(params)) return '';
  const normalized = normalizeRecord(params);
  return qs.stringify(normalized, { arrayFormat: 'brackets' });
}

function isPlainParams(value: unknown): value is Params {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeRecord(params: Params): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    const normalized = normalizeValue(value);
    if (normalized !== undefined) {
      out[key] = normalized;
    }
  }
  return out;
}

function normalizeValue(value: ParamValue): unknown {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
  }
  if (Array.isArray(value)) {
    const out = value
      .map((v) => normalizeValue(v))
      .filter((v): v is Exclude<unknown, undefined> => v !== undefined);
    return out.length > 0 ? out : undefined;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  return value;
}
